import { useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ClipboardItem, ListIssueFieldTransformed, PasteMetadata } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPromise = Promise<any>;

export function useGenericClipboardHandler<T>(
	field: ListIssueFieldTransformed,
	getValue: (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => T | null,
	saveValue: (value: T | null, meta: null, analyticsEvent: UIAnalyticsEvent) => AnyPromise,
	emptyValue?: T,
) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const pasteHandler = useCallback(
		async (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => {
			try {
				const value = getValue(clipboardItem, metadata);

				if (!value || (Array.isArray(value) && !value.length)) {
					return false;
				}

				await saveValue(
					value,
					null,
					createAnalyticsEvent({
						action: 'pasted',
						actionSubject: 'field',
						actionSubjectId: field?.type,
					}),
				);

				return true;
			} catch (e) {
				return false;
			}
		},
		[getValue, saveValue, createAnalyticsEvent, field?.type],
	);

	const cutHandler = useCallback(async () => {
		try {
			await saveValue(
				emptyValue ?? null,
				null,
				createAnalyticsEvent({
					action: 'cut',
					actionSubject: 'field',
					actionSubjectId: field?.type,
				}),
			);

			return true;
		} catch (e) {
			return false;
		}
	}, [saveValue, emptyValue, createAnalyticsEvent, field?.type]);

	return { pasteHandler, cutHandler };
}
